/** The season's matches, week by week, with results; and your match, with its Ready button */
import { getMatch, getOpponent, type DraftLeague, type DraftMatch } from '@ogreclan/league-core';
import { useEffect, useState } from 'preact/hooks';
import { api } from '../api';
import type { LeagueProps } from '../pages/league';
import { ActionButton } from '../ui';
import { playerName, you as findYou } from './common';

const PLAY = 'https://play.ogreclan.com';

/** Whether these Showdown users are online, checked every 15 seconds */
export function useOnline(ids: string[]) {
	const [online, setOnline] = useState<Record<string, boolean>>({});
	const key = ids.join(',');
	useEffect(() => {
		if (!key) return;
		let stopped = false;
		const load = () => api.get<{ online: Record<string, boolean> }>(`/api/showdown/online?ids=${encodeURIComponent(key)}`)
			.then(r => !stopped && setOnline(r.online), () => {});
		void load();
		const timer = setInterval(load, 15_000);
		return () => {
			stopped = true;
			clearInterval(timer);
		};
	}, [key]);
	return online;
}

/**
 * A player's name with a dot for whether they're on play.ogreclan.com. With `as` (the Showdown name linked to their
 * account, which is the one checked), it's shown beside the name, so being on another name doesn't look like a bug.
 */
export function OnlineDot({ online, name, as }: { online: boolean | undefined, name: string, as?: string | null }) {
	return <span class="nowrap" title={online ? `${name} is online on play.ogreclan.com${as ? ` as ${as}` : ''}` :
		`${name} isn't logged in to play.ogreclan.com${as ? ` as ${as}, the Showdown name linked to their account` : ' right now'}`}>
		<span style={`color:${online ? 'var(--good)' : 'var(--muted)'}`}>{online ? '●' : '○'}</span> {name}{as ? ` (${as})` : ''}
	</span>;
}

/** Your next unplayed match, up to this week */
function nextMatch(league: DraftLeague, playerId: string) {
	for (let week = 1; week <= league.week; week++) {
		const match = getMatch(league, week, playerId);
		if (match?.p2 && !match.result) return { week, match };
	}
	return null;
}

function YourMatch({ view, act }: LeagueProps) {
	const { league } = view;
	const me = findYou(league, view.you);
	const next = me && league.phase === 'season' ? nextMatch(league, me.id) : null;
	const opponentId = next ? getOpponent(next.match, me!.id) : null;
	const online = useOnline(next && opponentId ? [me!.id, opponentId] : []);
	if (!me || !next || !opponentId) return null;
	const { week, match } = next;
	const ready = match.ready || [];
	const opponent = playerName(league, opponentId);
	const room = match.live?.room;
	return <div class="card" style="outline:2px solid var(--accent)">
		<h3 style="margin-top:0">Your week {week} match: vs {opponent}</h3>
		<p class="row">
			<OnlineDot online={online[me.id]} name="You" />
			<span class="muted">·</span>
			<OnlineDot online={online[opponentId]} name={opponent} />
		</p>
		{room ? <p class="notice good">It's being played: <a href={`${PLAY}/${room}`} target="_blank" rel="noreferrer">go to the battle</a>.</p> :
			!me.teams[week] ? <p class="notice">Save your week {week} team first, on the Teams tab.</p> : <>
				<p>{ready.includes(me.id) ? <strong>You're ready.</strong> : `You're not ready yet.`}{' '}
					{ready.includes(opponentId) ? `${opponent} is ready.` : `${opponent} isn't ready yet.`}</p>
				<div class="row">
					{ready.includes(me.id) ?
						<ActionButton action={() => act('unready', `${week}`, `You're no longer ready.`)}>Not ready</ActionButton> :
						<ActionButton class="primary" action={() => act('ready', `${week}`)}>Ready!</ActionButton>}
				</div>
				<p class="muted small">When you're both ready and both online on <a href={PLAY} target="_blank" rel="noreferrer">play.ogreclan.com</a>,
					the battle starts there with your saved teams. You can press Ready here and log in to Showdown afterwards.</p>
			</>}
	</div>;
}

export function ScheduleTab(props: LeagueProps) {
	const { view } = props;
	const { league } = view;
	const name = (id: string, match: DraftMatch) => {
		const won = match.result?.winner === id;
		const text = <>{playerName(league, id)}{!match.result && !match.live && match.ready?.includes(id) &&
			<span class="badge good" title="Ready">ready</span>}</>;
		return id === view.you ? <strong>{text}{won ? ' ✓' : ''}</strong> : won ? <strong>{text} ✓</strong> : text;
	};
	return <>
		<YourMatch {...props} />
		<div class="grid">
			{league.schedule.map((matches, i) => {
				const week = i + 1;
				const current = league.phase === 'season' && week === league.week;
				return <div key={week} class="card" style={current ? 'outline:2px solid var(--accent)' : ''}>
					<h3 style="margin-top:0">Week {week} {current && <span class="badge accent">This week</span>}</h3>
					<table><tbody>
						{matches.map((match, j) => <tr key={j} class={match.p1 === view.you || match.p2 === view.you ? 'you' : ''}>
							{match.p2 ? <>
								<td>{name(match.p1, match)}</td>
								<td class="num nowrap">{match.result ? `${match.result.score[0]}–${match.result.score[1]}` : match.live ?
									<a class="badge good" href={`${PLAY}/${match.live.room}`} target="_blank" rel="noreferrer">Playing</a> : 'vs'}</td>
								<td>{name(match.p2, match)}</td>
							</> : <td colSpan={3}>{name(match.p1, match)} <span class="muted">has a bye</span></td>}
						</tr>)}
					</tbody></table>
					{matches.some(m => m.result?.manual) && <p class="muted small">Some results were entered by room staff.</p>}
				</div>;
			})}
		</div>
	</>;
}
